/**
 * Advanced plan settings: re-arrange habitats, the zoo boundary, extra space for animals, the terrain
 * background and the shared animal data table.
 */
import { useState } from 'react';
import type { PlanDoc } from '../../../../shared/types';
import { TERRAIN_DEFAULTS } from '../../../render/terrain';
import { useEditor, type EditorStore } from '../../../store/editor';
import { rearrangeAll } from '../../../tools/edits';
import { NumberField } from '../fields';
import { SpeciesTable } from './SpeciesTable';

/** The panel. */
export function PlanSettings({ store }: { store: EditorStore }) {
  const extra = useEditor(store, (s) => s.doc.settings.extraSpacePct);
  const terrain = useEditor(store, (s) => s.doc.terrain);
  const hasPlot = useEditor(store, (s) => !!s.doc.plot);
  const species = useEditor(store, (s) => s.species);
  const [table, setTable] = useState(false);
  const setTerrain = (t: PlanDoc['terrain'], label: string, group?: string) =>
    store.apply((d: PlanDoc) => { d.terrain = t; }, undefined, { label, ...(group ? { group } : {}) });

  return (
    <section className="panel">
      <h3>Plan</h3>
      <div className="actions">
        <button type="button" disabled={!species} onClick={() => rearrangeAll(store)} title="Re-slice every unlocked habitat around the paths and water">Re-arrange habitats</button>
        <button type="button" disabled={!species} onClick={() => setTable(true)}>Animal data…</button>
      </div>
      <div className="actions">
        {hasPlot && <button type="button" onClick={() => { store.setTool('select'); store.select({ kind: 'plot' }); }}>Edit boundary</button>}
        <button type="button" onClick={() => store.setTool('boundary')}>{hasPlot ? 'Draw a new boundary' : 'Draw the boundary'}</button>
      </div>
      <label className="field" style={{ marginTop: '0.6rem' }}>
        <span>Extra space for animals (%)</span>
        <span className="row">
          <NumberField value={extra} min={0} max={200} step={5} label="Extra space for animals" onChange={(v) => store.apply((d: PlanDoc) => { d.settings.extraSpacePct = v; }, undefined, { label: 'Extra space', group: 'extra' })} />
          <span className="muted small">on top of what the game asks for</span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={!!terrain?.show} onChange={(e) => setTerrain({ ...(terrain ?? { ...TERRAIN_DEFAULTS, show: false }), show: e.target.checked }, e.target.checked ? 'Show terrain' : 'Hide terrain')} />
        {' '}Terrain background (for ideas only)
      </label>
      {terrain?.show && (
        <>
          <label className="field">
            <span>Hilliness: {terrain.hilliness} m</span>
            <input type="range" min={0} max={80} value={terrain.hilliness} onChange={(e) => setTerrain({ ...terrain, hilliness: Number(e.target.value) }, 'Terrain hilliness', 'terrain-h')} />
          </label>
          <label className="field">
            <span>Band height: {terrain.band} m</span>
            <input type="range" min={1} max={10} value={terrain.band} onChange={(e) => setTerrain({ ...terrain, band: Number(e.target.value) }, 'Terrain bands', 'terrain-b')} />
          </label>
        </>
      )}
      {table && <SpeciesTable store={store} onClose={() => setTable(false)} />}
    </section>
  );
}
